import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CardQuest from "../../components/common/Card/CardQuest";
import Button from "../../components/common/Button/Button";
import CheckboxItem from "../../components/common/CheckBox/CheckboxItem";
import {
    useFavoritesQuestMutation,
    useLazyGetQuestListQuery,
} from "../../services/apiSlice";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { useTranslation } from "react-i18next";
import NewHeader from "../Home/NewHeader";

const Quest = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get("tab") || "";

    const observerRef = useRef(null);

    const [state, setState] = useState({
        activeTab: tabParam || "",
        isMenuOpen: false,
        page: 1,
        limit: 10,
        hasMore: true,
        searchQuery: "",
        showFavoritesOnly: false,
    });

    const [favorites, { isLoading }] = useFavoritesQuestMutation();
    const [getListQuests, { data, isFetching }] = useLazyGetQuestListQuery();

    const filterOptions = [
        { label: t("base.all"), count: 99 },
        { label: t("quest.dineIn"), count: 99 },
        { label: t("quest.delivery"), count: 99 },
        { label: t("quest.takeAway"), count: 99 },
    ];

    useEffect(() => {
        getListQuests({
            page: state.page,
            limit: state.limit,
        });
    }, [getListQuests, state.page, state.limit, state.activeTab, t]);

    useEffect(() => {
        if (!state.hasMore || !data?.total) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setState((prevState) => {
                        if (prevState.limit >= data?.total) {
                            return { ...prevState, hasMore: false };
                        }
                        return { ...prevState, limit: prevState.limit + 5 };
                    });
                }
            },
            { threshold: 1.0 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => observer.disconnect();
    }, [state.hasMore, data?.total]);

    const handleCheck = (label, count, isChecked) => {
        // setCheckedItems((prev) => {
        //   const updated = { ...prev };
        //   if (isChecked) {
        //     updated[label] = count;
        //   } else {
        //     delete updated[label];
        //   }
        //   return updated;
        // });
    };

    const handleFavoriteQuest = async (id) => {
        try {
            const response = await favorites({ quest_id: id }).unwrap();
            if (response.status === 200) {
                getListQuests({
                    page: state.page,
                    limit: state.limit,
                    status:
                        state.activeTab === t("base.all")
                            ? ""
                            : state.activeTab.toUpperCase(),
                }); // Refresh the quest list
            }
        } catch (err) {
            console.error("Failed to favorite quest:", err);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const activeTabFromParams = searchParams.get("activeTab");

        if (activeTabFromParams) {
            setState((prev) => ({
                ...prev,
                activeTab: activeTabFromParams,
            }));
        }
    }, [location.search]);

    return (
        <div className="flex flex-col mx-auto w-full max-w-[480px] ">
            <LoadingCommon statusLoading={isFetching || isLoading} />
            <div className="sticky top-0 left-0 w-full bg-white z-50">
                <NewHeader />
            </div>

            <div className="flex flex-col mt-6 w-full gap-6 px-[30px]">
                {(() => {
                    const doingQuests = (data?.data || []).filter(
                        (quest) => quest?.consumer_status === "DOING"
                    );
                    const notStartedQuests = (data?.data || []).filter(
                        (quest) => quest?.consumer_status === "NOT_STARTED"
                    );

                    const renderQuestCard = (quest) => {
                        const timerKey = `timer_${quest.id}`;
                        const storedTimer = localStorage.getItem(timerKey);
                        const remainingTime = storedTimer
                            ? parseInt(storedTimer, 10)
                            : null;
                        return (
                            <CardQuest
                                key={quest.id}
                                onClick={() =>
                                    navigate(
                                        `/quest/details/${quest.id}?activeTab=${state.activeTab}`,
                                        {
                                            state: {
                                                activeTab: state.activeTab,
                                            },
                                        }
                                    )
                                }
                                {...quest.quest_business}
                                consumerStatus={quest?.consumer_status}
                                handleFavoriteQuest={() =>
                                    handleFavoriteQuest(quest.id)
                                }
                                isFavorited={quest.is_favorited}
                                businessId={quest.quest_business_id}
                                thumbnails={
                                    quest.thumbnails?.map(
                                        (thumbnail) => thumbnail.file_path
                                    ) || []
                                }
                                est_time={quest.quest_business.est_time}
                                expire_time={quest.quest_business.expire_time}
                                address={quest.location.address}
                                businessAvatar={
                                    quest?.quest_business?.business?.avatar_data
                                        ?.file_path
                                }
                                slotAvailable={quest.slot_available}
                                businessName={
                                    quest.quest_business.business.company_name
                                }
                                remainingTime={
                                    remainingTime ? remainingTime : null
                                }
                            />
                        );
                    };

                    return (
                        <>
                            {doingQuests.length > 0 && (
                                <div className="mt-6 ">
                                    <div className="flex flex-col gap-3 self-stretch justify-start w-full mb-6">
                                        <div className="text-[#EF4056] font-bold text-2xl">
                                            {t("home.pausedQuests")}
                                        </div>
                                        <div className="font-medium text-base text-[#A0A0A0]">
                                            {t("home.completeWithinTimeLimit")}
                                        </div>
                                    </div>
                                    {doingQuests.map(renderQuestCard)}
                                </div>
                            )}

                            {notStartedQuests.length > 0 && (
                                <div className="mt-6">
                                    <div className="flex flex-col gap-3 self-stretch justify-start mb-6">
                                        <div className="text-[#253FF6] font-bold text-2xl">
                                            {t("home.newQuests")}
                                        </div>
                                    </div>
                                    {notStartedQuests.map(renderQuestCard)}
                                </div>
                            )}
                            {doingQuests.length === 0 &&
                                notStartedQuests.length === 0 && (
                                    <div className="flex flex-col items-center justify-center mt-12 py-12">
                                        <div className="text-[#A0A0A0] text-lg font-medium text-center">
                                            {t("quest.noQuests")}
                                        </div>
                                    </div>
                                )}
                        </>
                    );
                })()}
                {state.hasMore && (
                    <div
                        ref={observerRef}
                        className="h-10 w-full bg-transparent"
                    ></div>
                )}
            </div>
            <div
                className={`fixed top-0 right-0 h-screen w-full max-w-[340px] px-4 bg-white shadow-lg transform ${
                    state.isMenuOpen ? "translate-x-0" : "translate-x-full"
                } transition-transform duration-300 ease-in-out z-50`}
            >
                <div className="flex gap-10 justify-between items-center w-full font-medium whitespace-nowrap">
                    <Button
                        text={t("base.apply")}
                        className="gap-3 self-stretch px-2.5 py-3 my-auto text-white bg-[#253FF6] rounded-xl min-h-[40px] w-[85px]"
                        onClick={() => {}}
                    />
                    <Button
                        text={t("base.reset")}
                        onClick={() => {}}
                        className="gap-3 self-stretch px-2.5 py-3 my-auto rounded-xl border border-solid border-[#A0A0A0] min-h-[40px] text-[#A0A0A0] w-[85px]"
                    />
                </div>
                <div className="flex flex-col p-2.5 mt-2.5 w-full rounded-xl text-[#A0A0A0]">
                    <div className="flex gap-2.5 items-center w-full font-medium whitespace-nowrap text-zinc-800">
                        <div className="gap-1.5 self-stretch my-auto">
                            {t("quest.type")}
                        </div>
                    </div>
                    {filterOptions.map((option, index) => (
                        <CheckboxItem
                            key={index}
                            label={option.label}
                            count={option.count}
                            onCheck={handleCheck}
                        />
                    ))}
                </div>
            </div>
            {state.isMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() =>
                        setState((prev) => ({ ...prev, isMenuOpen: false }))
                    }
                ></div>
            )}
        </div>
    );
};

export default Quest;
